import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IconLoader, IconArrowLeft, IconUser } from "@tabler/icons-react";
import PersonForm from "../../components/PersonForm";

interface Person {
    id: string;
    firstName: string;
    lastName: string;
    phone: string;
    address: string;
    houseNumber: string;
    email: string;
    city: string;
    psc: string;
    rodCislo?: string;
    type: 'PARENT' | 'ADULT' | 'KID';
    gender?: 'MALE' | 'FEMALE';
    schoolyearId?: string;
    isBernolakova?: boolean;
    schoolName?: string;
}

const EditPerson = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [person, setPerson] = useState<Person | null>(null);
    const [loading, setLoading] = useState(true);
    const [deleteConfirm, setDeleteConfirm] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch person data
                const personRes = await fetch(`${process.env.REACT_APP_PATH}/api/user/data/profile`, {
                    credentials: 'include',
                });

                if (!personRes.ok) {
                    throw new Error('Failed to fetch profile');
                }

                const userData = await personRes.json();
                const foundPerson = userData.data.person.find((p: Person) => p.id === id);

                if (!foundPerson) {
                    navigate('/main');
                    return;
                }

                setPerson(foundPerson);
            } catch (error) {
                console.error('Error fetching data:', error);
                navigate('/main');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, navigate]);

    const handleSuccess = () => {
        navigate('/main');
    };

    const handleCancel = () => {
        navigate('/main');
    };

    const handleDelete = async () => {
        if (!deleteConfirm) {
            setDeleteConfirm(true);
            return;
        }

        try {
            // Re-fetch profile to validate business rules before deleting
            const profileRes = await fetch(`${process.env.REACT_APP_PATH}/api/user/data/profile`, {
                credentials: 'include',
            });

            if (!profileRes.ok) {
                throw new Error('Failed to validate delete operation');
            }

            const profileData = await profileRes.json();
            const persons: Person[] = profileData.data.person || [];

            const mainCount = persons.filter(p => p.type === 'PARENT' || p.type === 'ADULT').length;
            const target = persons.find(p => p.id === id);

            if (!target) {
                alert('Osoba nebola nájdená.');
                setDeleteConfirm(false);
                return;
            }

            // Do not allow removing the main account person if it would leave no PARENT/ADULT
            if ((target.type === 'PARENT' || target.type === 'ADULT') && mainCount <= 1) {
                alert('Nie je možné odstrániť hlavnú osobu účtu. Najprv pridajte inú osobu typu rodič/dospelý alebo kontaktujte podporu.');
                setDeleteConfirm(false);
                return;
            }

            // Proceed with delete
            const response = await fetch(`${process.env.REACT_APP_PATH}/api/user/person/${id}`, {
                method: 'DELETE',
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error('Failed to delete person');
            }

            navigate('/main');
        } catch (error) {
            console.error('Error deleting person:', error);
            alert('Chyba pri odstraňovaní osoby');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="flex items-center">
                    <IconLoader className="h-8 w-8 animate-spin text-blue-600 mr-4" />
                    <span className="text-lg text-gray-700">Načítavam údaje...</span>
                </div>
            </div>
        );
    }

    if (!person) {
        return null;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <button
                            onClick={() => navigate('/main')}
                            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                        >
                            <IconArrowLeft className="h-5 w-5 mr-2" />
                            Späť na profil
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-white rounded-lg shadow-sm border ">

                    <div className="flex items-center p-6 pb-0">
                        <div className="flex items-center justify-center h-10 w-10 rounded-full bg-blue-100 mr-4">
                            <IconUser className="h-6 w-6" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Upraviť osobu</h1>

                        </div>
                    </div>
                    <div className="p-6">
                        <PersonForm
                            initialData={person}
                            onSuccess={handleSuccess}
                            onCancel={handleCancel}
                            onDelete={deleteConfirm ? handleDelete : () => setDeleteConfirm(true)}
                            showDelete={true}
                            isEmbedded={true}
                            userEmail={person.email}
                        />
                    </div>


                    {deleteConfirm && (
                        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-700">
                                Naozaj chcete odstrániť túto osobu? Táto akcia je nevratná.
                            </p>
                            <div className="mt-3 flex gap-2">
                                <button
                                    onClick={handleDelete}
                                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                                >
                                    Áno, odstrániť
                                </button>
                                <button
                                    onClick={() => setDeleteConfirm(false)}
                                    className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400"
                                >
                                    Zrušiť
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditPerson;
